import { CasingPlanModel } from "../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { Address } from "../../common/models/address.model";
import { ComplianceReportFile, ComplianceReportStatus, ComplianceReportWell } from "../../issues/models/compliance.model";
import { IUploadFile } from "../services/document-management.service";

export interface GetWellsRequest {
    wellIds: string[];
    viewRequested?: DataViewRequest
}

export enum DataViewRequest {
    NotSpecified,
    External7AOutboundView,
    ExternalApprovedPermitsView,
    OrganizationComplianceReporting,
    ReviewComplianceReport,
    ReviewIssue
}

export interface WellStub {
    id: string;
    organizationId: string;
    historicalOwnerNumber: string;
    faOrganizationId: string;
    compliance: Compliance;
    dateCompleted: string;
    lease: Lease;
    location: WellLocation;
    name: string;
    wellNumber: string;
    wellStatus: WellStatus;
    permitNumber: string;
    plugging: Plugging;
}

export interface StratigraphicWellBondingRequirement {
    id: number;
    apiNumber: string;
    amountRequired?: number;
    requiredBy?: Date;
    effectiveFrom: Date;
    effectiveTo: Date;
    enteredBy: string;
    enteredOn: Date;
    isPending: boolean;
    isDeleted: boolean;
}

export interface UpdateStratigraphicWellBondingRequirementRequest {
    requirements: StratigraphicWellBondingRequirement[];
}

export interface Well extends WellStub {
    organizationName: string;
    originalOrganizationId: string;
    alternateNumbers: string[];
    apiComponents: ApiComponents;
    constructionPermit: ConstructionPermit;
    isMultiLatituderal: boolean;
    dateWellDrillingStarted: string;
    dateTotalDepthReached: Date;
    dateLastModified: string;
    dateOfAuthorizationToTransport: string;
    dateOfConfidentialDataPeriodEnd: string;
    dateOfHookupInspection: string;
    dateOfOriginalSpud: string;
    dateOfRevocation: string;
    dateNextInspectionDue: string;
    dateReceivedCompletionReport: string;
    wellCompletion: string;
    directionalSurvey: DirectionalSurvey;
    ohioPeperNumber: string;
    drillingContractorId: number;
    drillingContractor: string;
    organizationsWellNumber;
    stateProjectNumber: string;
    rigType: string;
    odnrDistrict: string;
    aapgBasinCode: number;
    oilFieldNumber: number;
    deepestFormationPentrated: string;
    commingling: Commingling;
    surfaceOwnership: string;
    initialShutInPressure: number;
    areSamplesRequired: boolean;
    cathodicProtection: string;
    hasOpenPitOnSite: boolean;
    hasFederalMiningInterest: boolean;
    hasStateMineralInterest: boolean;
    referenceForConstruction: string;
    referenceForFormationTops: string;
    depths: Depths;
    isAuthorizedByRule: boolean;
    odnrCurrentClass: string;
    odnrOriginalClass: string;
    newOrExisting: string;
    disposal: Disposal;
    dateOfWaterAnalysis: string;
    annulus: Annulus;
    injection: Injection;
    acresInDrillingUnit: number;
    drillingUnitDescription: string;
    production: Production;
    h2sGasPresent: boolean;
    comment: string;
    mechanicalIntegrity: MechanicalIntegrity;
    inspectionFrequency: number;
    uicMonitoringReportFrequency: number;
    frequencyOfIdleWellReports: number;
    lastPermitIssuedApplicationType: string;
    forms: Form[];
    fracFocus: boolean;
    mapSym: string;
    mapSymbol: string;
    newP_Rest: boolean;
    originalOwnerName: string;
    preCompReportDate: string;
    proposedFormation: string;
    salesCD: number;
    scanImageNumber: number;
    drillersTotalDepth: string;
    loggersTotalDepth: string;
    injectionInterval: InjectionInterval;
    construction: string;
    casingTesting: string;
    injectionZone: InjectionZone;
    casingPlan: CasingPlanModel;
    wellCardImageExists: boolean;
	variance: string;
	originalType: string;
	plugReportReceivedFromOperator?: boolean;
	vaultedVented?: boolean;
    ownerHistory: WellOwnerHistory[];
    bondingRequirements: StratigraphicWellBondingRequirement[];
    activeBondingRequirement: StratigraphicWellBondingRequirement;
	wellComplianceData: WellComplianceData;
	formations: Formation[];
	casingTubing: CasingTubing[];
}

export interface ProductionReportingSearchSpec {
    apiNumber?: string;
    reportingYear?: number;
    reportingQuarter?: number;
    ricsOwnerNumber?: string;
    rbdmsOwnerNumber?: string;
}

export interface WellProductionReporting {
	apiNumber: string;
	reportingYear: number;
	quarter: number;
	ricsOwnerNumber: string;
	relatedReportId: string;
	rbdmsOwnerNumber: string;
	ownerName: string;
	oilProduced?: number;
	gasProduced?: number;
	brineProduced?: number;
	oilSold?: number;
	gasSold?: number;
	daysInProduction?: number;
	dateOfFirstProduction?: Date;
	reportedDate?: Date;
	modifiedDate?: Date;
	domesticUserAddress?: string;
	domesticUserCity?: string;
	domesticUserState?: string;
	domesticUserZipCode?: string;
	domesticUserName?: string;
	domesticUserPhone?: string;
	numberOfTanks?: number;
	maximumStorageCapacity?: number;
	wellName: string;
	wellNumber: string;
	approvedBy?: string;
	enteredBy?: string;
	productionComments?: string;
	dateAdded?: Date;
}

export interface WellWithFinancialAssurance extends Well {
    readonly requiresBonding: boolean;
    readonly requiresInsurance: boolean;
}

export interface WellsRequiringFA {
    id: string;
    name: string;
    wellNumber: string;
    disposition: string;
    legacyTracking: string;
    organizationName: string;
    organizationId: string;
    countyName: string;
    townshipName: string;
    tract?: boolean;
    releaseOfLeases?: boolean;
    permittedUrban?: boolean;
    isUrban?: boolean;
    isHorizontal?: boolean;
    slant?: string;
    acresInDrillingUnit?: number;
    h2sGasPresent?: boolean;
    wellType?: string;
    wellTypeDescription?: string;
    proposedFormation?: string;
    deepestFormationPenetrated?: string;
    dateOfOriginalSpud?: Date;
    drillersMeasuredVerticalDepth?: number;
    dateTotalDepthReached?: Date;
    dateOfPlugBack?: Date;
    productionDateOfProduction?: Date;
    datePluggedAndAbandoned?: Date;
    loggerMeasuredVerticalDepth?: number;
    latitude83?: number;
    longitude83?: number;
    plugReportReceivedFromOperator?: boolean;
    hasBuyerFA?: boolean;
    requiresInsurance?: boolean;
    requiresBonding?: boolean;

}
export interface InjectionInterval {
    topDepth: string;
    bottomDepth: string;
    zoneGeology: string;
}

export interface InjectionZone {
    topFromGroundLevel: number;
    bottomFromGroundLevel: number;
    maximumPressure: string;
}

export interface SystemCheckDefinition {
    alertCategory: string;
    alertName: string;
    alertText: string;
    buffer: number;
    bufferMeasurement: string;
    query: string;
    systemCheckType: SystemCheckType;
}

export enum SystemCheckType {
    Basic,
    Advanced
}


// Willing to break these out if needed, but pretty sure they'll only be used by the above interface
export interface ApiComponents {
    completionNumber: number;
    county: number;
    holeNumber: number;
    sideTrack: number;
    state: number;
}


export interface ConstructionPermit {
    dateApproved: string;
    dateExpired: string;
}

export interface Plugging {
    datePluggingPlanApproved: string;
    datePluggedAndAbandoned: string;
    dateOfPlugBack: string;
}

export interface DirectionalSurvey {
    hasBeenReceived: boolean;
    hasBeenRun: boolean;
}

export interface Lease {
    id: string;
    name: string;
    originalId: string;
}

export interface Commingling {
    isDownHoleCommingled: boolean;
    isProductionCommingledAtSurface: boolean;
    dateSurfaceComminglingApproved: string;
}

export interface Depths {
    drillersMeasuredVerticalDepth: number;
    kickoffTrueVerticalDepth: number;
    loggerMeasuredVerticalDepth: number;
    plugbackMeasuredDepth: number;
    plugbackTrueVerticalDepth: number;
    trueVerticalDepth: number;
}

export interface Disposal {
    disposalCodes: string[];
    disposalFacilityNumber: string;
    disposalMethod: string;
    disposalWellId: string;
    hasCommercialDisposal: boolean;
}

export interface Compliance {
    dateOfLastReview: string;
    isCompliant: boolean;
}

export interface Annulus {
    datePressureMonitoringApproved: string;
    frequencyOfFluidAddition: number;
    maximumRequiredPressure: number;
    minimumRequiredPressure: number;
    specificGravityOfFluid: number;
    typeOfFluid: string;
}

export interface Injection {
    class: string;
    dateOfFirstInjection: string;
    isCorrosive: boolean;
    maxAllowablePressure: number;
    maxAllowableRate: number;
    phOfInjectate: number;
    specificGravityOfInjectate: number;
    typeOfFluid: string;
    undergroundInjectionControlPermit: string;
}


export interface Production {
    classification: string;
    cumulativePreMonthlyReportingProduction: number;
    dateOfFirstProduction: string;
    dateOfProduction: Date;
    initialProduction: ProductionTypes;
    initialProductionAfterTreatment: ProductionTypes;
    hadNaturalProductionPriorToStimulation: boolean;
    productionMethod: string;
}

export interface WellComplianceData {

    apiNumber: string;
    asDrilledPlatPreliminaryDate: Date;
    asDrilledPlatFinalDate: Date;
    boreholeDeviationSurveyDate: Date;
    revisedUnitDate: Date;
    asDrilledFee: Date;
    revisedUnitFeeDate: Date;
    pluggingReportReceivedDate: Date;
    form8PreliminaryReceivedDate: Date;
    form8FinalReceivedDate: Date;
    form8ASurfaceHoleReceivedDate: Date;
    form8BWellStimulationReceivedDate: Date;
    logsReceivedDate: Date;
    cementTicketReceivedDate: Date;
    cementLogReceivedDate: Date;
    stimInvoiceReceivedDate: Date;
    stimRecordReceivedDate: Date;
    stimLogReceivedDate: Date;
    safetyDataSheetReceivedDate: Date;
    prGraphReceivedDate: Date;
}


export interface ProductionTypes {
    oil: number;
    gas: number;
    brine: number;
}

export interface MechanicalIntegrity {
    annulusDemonstrationFrequency: string;
    dateOfNextExternalDemonstration: Date;
    dateOfNextInternalDemonstration: Date;
    dateOperatorNotifiedOfRequirements: Date;
    externalDemonstrationFrequency: number;
    internalDemonstrationFrequency: number;
    internalTestRequestedPressure: number;
}

export interface Form {
    isRequired: boolean;
    name: string;
    wasReceived: boolean;
}

export interface WellLocation {
    confidence: number;
    dateModified: string;
    directionsToWell: string;
    elevation: Elevation;
    isUrban: boolean;
    sourceOfLocation: string;
    slant: string;
    gisPoints: GISPoint[];
}

export interface Elevation {
    groundLevel: number;
    kellyBushing: number;
    derrickFloor: number;
    digitalElevationModel: number;
    target: number;
}

export interface LastTakePointLocation {
    coordinates: Coordinates;
    footageCallsFromSectionLines: string;
}

export interface Coordinates {
    statePlaneZone: string;
    statePlaneXCoordinate: number;
    statePlaneYCoordinate: number;
    statePlaneOriginalXCoordinate: number;
    statePlaneOriginalYCoordinate: number;
    latitude: number;
    longitude: number;
    nad83XCoordinate: number;
    nad83YCoordinate: number;
    longitude83: number;
    latitude83: number;
}

export interface ToeLocation {
    bearing: string;
    distance: number;
}

export interface County {
    apiNumber: number;
    name: string;
}

export enum GISPointTypes {
    WellHead = "wellHead",
    LastTakePoint = "lastTakePoint",
    FirstTakePoint = "firstTakePoint",
    Target = "target",
    Heel = "heel",
    Toe = "toe",
}

export interface GISPoint {
    identifier?: any;
    apiNumber: string;
    permitId: string;
    pointType: GISPointTypes;
    county: County;
    lot: string;
    section: number;
    cardinalPointSection: string;
    township: DirectionIdentifier;
    range: DirectionIdentifier;
    quarterTownship: number;
    footageCallsFromSectionLines: string;
    allotment: string;
    originalSurvey: string;
    tract: string;
    fraction: number;
    civilTownshipNumber: number;
    ogsQuadrangle: Quadrangle;
    coordinates: Coordinates;
    bearing: string;
    distance: number;
}

export interface Quadrangle {
    code: string;
    name: string;
}

export interface DirectionIdentifier {
    number: number;
    direction: string;
    name: string;
}

export interface WellStatus {
    dateLastUpdated?: Date;
    disposition: string;
    legacyTracking: string;
    permittingModifiers: number;
    type: WellType;
}



export interface WellType {
    type: string;
    description: string;
}

export enum WellPermittingModifiers {
    None = 0,
    ExemptMississippian= 1,
    ExemptDomestic= 2,
    ReleaseOfLease= 4,
    ConsideredUrbanForPermitting= 8,
    WithoutFiveAcres= 16,
    LastTransferPriorToJune2010= 32,
};

export interface WellOwnerHistory {
	identifier?: string;
    organizationId: string;
    organizationName: string;
    dateOwnershipTaken: Date;
    dateOwnershipRelinquished: Date;
}
export interface PendingWellTransfer {
    createdDate: string;
    applicationNumber: string;
    transfererCompanyName: string;
    transfereeCompanyName: string;
    flowId: string;
    status: string;
}

export interface WellSearchSpec {
    apiNumber?: string;
    county?: string;
	township?: string;
	RBDMSOwnerNumber?: string;
	RICSOrganizationId?: string;
}

export interface WellSearchResult {
	id: string;
	wellNumber: string;
	name: string;
	lease: Lease;
	status: WellStatus;
	organizationName: string;
	organizationId: string;
	historicalOwnerNumber: string;
	location: WellLocation;
	acresInDrillingUnit?: number;
	h2sGasPresent?: boolean;
	proposedFormation: string;
	deepestFormationPenetrated: string;
	dateOfOriginalSpud?: Date;
	dateTotalDepthReached?: Date;
	plugging: Plugging;
	production: Production;
	plugReportReceivedFromOperator?: boolean;
	vaultedVented?: boolean;
	depths: Depths;
	activeBondRequirement: StratigraphicWellBondingRequirement;
	requiresBonding: boolean;
	requiresInsurance: boolean;
}

export interface ReservedHoleNumbers {
    county: County;
    reserved: number[];
    nextAvailableCurrentHole?: number;
    nextAvailableHistoricHole?: number;
}

export interface ReserveAPIRequest  {
    apiComponents: ApiComponents;
	county: County;
	organizationName?: string;
	rbdmsOwnerNumber?: string;
	ricsOrganizationId?: string;	
	wellType?: string;
	wellTypeDescription?: string;
	
}

export interface OrphanWellRequest {
    requestedOrphanWell: Well;
    submitWellToGIS: boolean;
}


export interface WellLogSearchSpec {
    apiNumbers?: string[];
    logTypes?: string[];
}

export interface ComplianceSearchSpec {
    apiNumbers: string[];
}

export interface WellStimulationSearchSpec {
    apiNumbers: string[];
}

export interface WellLog {
    id: string;
    counter?: number;
    apiNumber: string;
    logType: string;
    ricsownerNumber: string;
    logCompanyNumber?: number;
    logCompany?: string;
    dateRun?: Date;
    dateReceived?: Date;
    confidential: boolean;
    dateConfidential?: Date;
    elevation?: number;
    measFr?: string;
    temp?: number;
    formationCodeTop?: string;
    formationCodeBottom?: string;
    top?: number;
    bottom?: number;
    ynMissing?: boolean;
    annotatedLog: boolean;
    dateSepia?: Date;
    dateDigital?: Date;
    logsRun: string;
    las?: boolean;
    tifName: string;
    comments: string;
    dateModified?: Date;
}

export interface Compliance {
    id: string;
    inspecId?: number;
    apiNumber?: string;
    currentOpNo?: number;
    dateViolation?: Date;
    dateNote?: Date;
    typeNote?: string;
    dateEnforce?: Date;
    enforcementType?: string;
    orderNumber?: number;
    documentNumber?: string;
    snc?: string;
    methSnc?: string;
    dateComplianceRequest?: Date;
    csWritten?: string;
    dateCompleted?: Date;
    dateFinal?: Date;
    dateWithdrawn?: Date;
    dateApfd?: Date;
    dateApcd?: Date;
    dateApad?: Date;
    dateAssessed?: Date;
    assessed?: number;
    dateCollected?: Date;
    collected?: string;
    appelNumber?: string;
    attorney?: string;
    dateHearing?: Date;
    resultAppeal?: string;
    resultCc?: string;
    comment?: string;
    dateModified?: Date;
    upsizeTs?: number[];
    chiefOrder?: string;
    caseNumber?: string;
    novNumber?: string;
    year?: number;
    numberOrder?: number;
    image?: string;
    issueNumber?: number;
    subjectLine?: string;
    sendToWebDate?: Date;
    dateSigned?: Date;
    resolution?: string;
    signed?: Date;
    terminated?: Date;
    vacated?: Date;
    dateRevocation?: Date;
}

export interface WellStimulation {
    id: string;
    apiNumber: string;
    dateTreated?: Date;
    top?: number;
    bottom?: number;
    stimulationFormationCode?: string;
    topFormation?: number;
    bottomFormation?: number;
    stimulationHole?: string;
    ohTop?: number;
    ohBase?: number;
    fracAcid?: string;
    units?: string;
    volume?: number;
    lbsProp?: number;
    acidConcentration?: number;
    mbpress?: number;
    mtPress?: number;
    misip?: number;
    _5sip?: number;
    bhp?: number;
    comment?: string;
    dateModified?: Date;
    upsizeTs?: number[];
    fracWit: boolean;
    bopType?: string;
    pressRateGraph?: boolean;
    record?: boolean;
    invoice?: boolean;
    fracDate?: Date;
    averageRate?: number;
    fracCo?: number;
    circulation?: string;
    valvesClosed?: string;
    inspector?: number;
    stage?: number;
    deleteRecord?: boolean;
    dateStarted?: Date;
    dateEnded?: Date;
}

export interface InspectionSearchSpec {
    apiNumbers: string[];
    inspectionTypes: string[];
    includeLetters: boolean;
    includeAddresses: boolean;
}

export interface Inspection {
    id: number;
    inspectionNumber?: string;
    apiNumber?: string;
    inspectionType?: string;
    inspectionPurpose?: string;
    dateInspected?: Date;
    dateRemedyRequired?: Date;
    dateExtension?: Date;
    dateNov?: Date;
    notificationType?: string;
    datePassed?: Date;
    duration?: number;
    inspector?: number;
    violation: boolean;
    snc: boolean;
    incidentNumber?: string;
    complianceId?: string;
    responsibleCompanyNumber?: string;
    rigNumber?: string;
    meterNumber?: string;
    injectionPressure?: number;
    annulusPressure?: number;
    flowMeter?: number;
    haulerNumber?: number;
    pitsFilled: boolean;
    drillingEquipment: boolean;
    productionEquipment: boolean;
    debrisRemoved: boolean;
    erosion: boolean;
    roadways: boolean;
    landownerWaiver: boolean;
    comment?: string;
    dateModified?: Date;
    PRestPass: boolean;
    FRestPass: boolean;
    upsizeTs?: number[];
    district?: string;
    dateNotify?: Date;
    datePerformed?: Date;
    dateRemedy?: Date;
    dateRequired?: Date;
    dateScheduled?: Date;
    stFldNumber?: number;
    permit?: string;
    novNumber?: string;
    chiefOrder?: string;
    caseNumber?: string;
    licenseNumber?: string;
    wellheadLatitude?: number;
    wellheadLongitude?: number;
    confidence?: number;
    tankLatitude?: number;
    tankLongitude?: number;
    directions?: string;
    wellheadElevation?: number;
    contactName?: string;
    contactPhone?: string;
    contactFax?: string;
    actualVac?: boolean;
    annulusVac?: boolean;
    flowVac?: boolean;
    airMi?: boolean;
    waterMi?: boolean;
    structuresMi?: boolean;
    peopleMi?: boolean;
    airMo?: boolean;
    waterMo?: boolean;
    structuresMo?: boolean;
    peopleMo?: boolean;
    airMa?: boolean;
    waterMa?: boolean;
    structuresMa?: boolean;
    peopleMa?: boolean;
    offsiteImpact?: string;
    landMi?: boolean;
    landMo?: boolean;
    landMa?: boolean;
    remedyRequired?: string;
    wellName?: string;
    wellNumber?: string;
    historicalOwner?: string;
    wellheadCivilTownshipNumber?: number;
    wellheadSection?: number;
    wellheadLot?: string;
    datePrinted?: Date;
    inspectorOperatorNumber?: number;
    fracWit?: boolean;
    fracInspectionNotify?: boolean;
    entryLatitude?: number;
    entryLongitude?: number;
    deleteRecord?: boolean;
    companyOnlyViolation?: boolean;
    approvedDate?: Date;
    approvedBy?: string;
    addresses?: Address[];
    inspectionLetters?: InspectionLetter[];
}

export interface InspectionLetter {
    id: number;
    inspectionId: number;
    letterNumber: number;
    letter?: string;
    dateSent?: Date;
    dateReceived?: Date;
    dateDue?: Date;
}


export interface Formation {
	id: number;
	apiNumber: string;
	isFinal: boolean;
	prodcingFormation?: boolean;
	aapgCode: string;
	methodObtained: string;
	selectedFormation: string;
	top: number;
	base?: number;
	showsOf: string;
	remarks: string;
	show?: boolean;
}

export class EditedFormationRecord implements Formation {
	id: number;
	apiNumber: string;
	isFinal: boolean;
	producingFormation?: boolean;
	aapgCode: string;
	methodObtained: string;
	selectedFormation: string;
	top: number;
	base?: number;
	showsOf: string;
	remarks: string;
	show?: boolean;

	comments: string;

	constructor() { }

	toFormation(): Formation {
		let formation: Formation = {
			id: this.id,
			apiNumber: this.apiNumber,
			isFinal: this.isFinal,
			producingFormation: this.producingFormation,
			aapgCode: this.aapgCode,
			methodObtained: this.methodObtained,
			selectedFormation: this.selectedFormation,
			top: this.top,
			base: this.base,
			showsOf: this.showsOf,
			remarks: this.remarks,
			show: this.show
		} as Formation;
		return formation;
	}
}

export interface CasingTubing {
	id: number;
	apiNumber: string;
	type: string;
	isFinal: boolean;
	wellboreDiameter?: number;
	casingDiameter?: number;
	collarDiameter?: number;
	feetInstalled?: number;
	feetLeftInWell?: number;
	cementVolume?: number;
	cementTop?: number;
	grade?: number;
	weight?: number;
	burstPressureRating?: number;
	toolType: string;
	bottom: number;
}

export class EditedCasingTubingRecord implements CasingTubing {
	id: number;
	apiNumber: string;
	type: string;
	isFinal: boolean;
	wellboreDiameter?: number;
	casingDiameter?: number;
	collarDiameter?: number;
	feetInstalled?: number;
	feetLeftInWell?: number;
	cementVolume?: number;
	cementTop?: number;
	grade?: number;
	weight?: number;
	burstPressureRating?: number;
	toolType: string;
	bottom: number;

	comments: string;

	toCasingTubing(): CasingTubing {
		let record: CasingTubing = {
			id: this.id,
			apiNumber: this.apiNumber,
			isFinal: this.isFinal,
			type: this.type,
			wellboreDiameter: this.wellboreDiameter,
			casingDiameter: this.casingDiameter,
			collarDiameter: this.collarDiameter,
			feetInstalled: this.feetInstalled,
			feetLeftInWell: this.feetLeftInWell,
			cementVolume: this.cementVolume,
			cementTop: this.cementTop,
			grade: this.grade,
			weight: this.weight,
			burstPressureRating: this.burstPressureRating,
			toolType: this.toolType,
			bottom: this.bottom
		} as CasingTubing;
		return record;
	}
}

